/**
 * capture-client-exception
 *
 * Safe client-side exception reporting for UI error boundaries.
 *
 * Primary responsibilities:
 * - Forward sanitized client exceptions to Sentry
 * - Capture enriched PostHog exception events with dedupe
 *
 * Dependencies:
 * - posthog-js
 * - exception telemetry helpers
 */

import posthog from 'posthog-js'

import {
  ActivationSurface,
  ExceptionTelemetryContext,
  ExceptionTelemetryPayload,
  buildExceptionTelemetryPayload,
  createTelemetryError
} from './exception-telemetry'

type PostHogExceptionClient = {
  capture?: (eventName: string, properties?: Record<string, unknown>) => unknown
  captureException?: (error: unknown, properties?: Record<string, unknown>) => unknown
  exceptions?: {
    buildProperties?: (
      error: unknown,
      metadata?: { handled?: boolean; syntheticException?: Error }
    ) => Record<string, unknown>
  }
  get_distinct_id?: () => string
  get_property?: (propertyName: string) => unknown
  get_session_id?: () => string
}

type ClientExceptionContextInput = string | ExceptionTelemetryContext

type BaseActivationExceptionContext = Omit<
  ExceptionTelemetryContext,
  'source' | 'activationSurface' | 'activationStage'
>

export interface CaptureClientExceptionResult {
  captured: boolean
  deduped: boolean
  payload: ExceptionTelemetryPayload
}

export interface PublicChatFirstResponseExceptionContext
  extends BaseActivationExceptionContext {
  source?: string
  stage?: 'first_response' | 'first_response_save'
}

export interface SharedChatFollowUpExceptionContext
  extends BaseActivationExceptionContext {
  source?: string
  stage?: 'follow_up_queue' | 'follow_up_send'
}

const CLIENT_EXCEPTION_DEDUPE_WINDOW_MS = 30_000
const CLIENT_EXCEPTION_DEDUPE_MAX_KEYS = 200
const clientExceptionDedupe = new Map<string, number>()

function getPostHogClient() {
  return posthog as unknown as PostHogExceptionClient
}

function readSessionStorageValue(key: string) {
  if (typeof window === 'undefined') return null

  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function readClientSessionId(client: PostHogExceptionClient) {
  try {
    return client.get_session_id?.() || readSessionStorageValue('analytics_session_id')
  } catch {
    return readSessionStorageValue('analytics_session_id')
  }
}

function readAnonymousId(client: PostHogExceptionClient) {
  try {
    return client.get_distinct_id?.() || null
  } catch {
    return null
  }
}

function readFirstSession(client: PostHogExceptionClient) {
  try {
    const value = client.get_property?.('$is_first_session')
    return typeof value === 'boolean' ? value : null
  } catch {
    return null
  }
}

function normalizeContext(
  contextInput: ClientExceptionContextInput,
  client: PostHogExceptionClient
): ExceptionTelemetryContext {
  const context =
    typeof contextInput === 'string' ? { source: contextInput } : contextInput

  return {
    ...context,
    anonymousId: context.anonymousId ?? readAnonymousId(client),
    sessionId: context.sessionId ?? readClientSessionId(client),
    firstSession: context.firstSession ?? readFirstSession(client)
  }
}

function buildSentryTags(payload: ExceptionTelemetryPayload) {
  const tags: Record<string, string> = {
    source: payload.source,
    severity: payload.severity,
    activation_surface: payload.activation_surface
  }

  if (typeof payload.activation_stage === 'string') {
    tags.activation_stage = payload.activation_stage
  }

  if (typeof payload.boundary === 'string') {
    tags.boundary = payload.boundary
  }

  return tags
}

function pruneDedupeCache(now: number) {
  if (clientExceptionDedupe.size <= CLIENT_EXCEPTION_DEDUPE_MAX_KEYS) return

  for (const [key, capturedAt] of clientExceptionDedupe.entries()) {
    if (now - capturedAt > CLIENT_EXCEPTION_DEDUPE_WINDOW_MS) {
      clientExceptionDedupe.delete(key)
    }
  }
}

function buildPostHogFallbackPayload(
  client: PostHogExceptionClient,
  telemetryError: Error,
  payload: ExceptionTelemetryPayload
) {
  try {
    const exceptionProperties = client.exceptions?.buildProperties?.(telemetryError, {
      handled: true,
      syntheticException: new Error('PostHog syntheticException')
    })

    return {
      ...(exceptionProperties ?? {}),
      ...payload
    }
  } catch {
    return payload
  }
}

export function shouldThrottleClientException(
  payload: ExceptionTelemetryPayload,
  now = Date.now()
) {
  const previousCapturedAt = clientExceptionDedupe.get(payload.dedupe_key)

  if (
    typeof previousCapturedAt === 'number' &&
    now - previousCapturedAt < CLIENT_EXCEPTION_DEDUPE_WINDOW_MS
  ) {
    return true
  }

  clientExceptionDedupe.set(payload.dedupe_key, now)
  pruneDedupeCache(now)

  return false
}

export function resetClientExceptionDedupeForTests() {
  clientExceptionDedupe.clear()
}

function captureSentryClientException(
  telemetryError: Error,
  payload: ExceptionTelemetryPayload
) {
  if (process.env.LOSI_SKIP_SENTRY === '1') return

  void import('@sentry/nextjs')
    .then(({ captureException }) => {
      captureException(telemetryError, {
        tags: buildSentryTags(payload),
        extra: payload
      })
    })
    .catch(() => {
      // Client error reporting must never break error boundaries.
    })
}

export function captureClientException(
  error: unknown,
  contextInput: ClientExceptionContextInput
): CaptureClientExceptionResult {
  const client = getPostHogClient()
  const context = normalizeContext(contextInput, client)
  const payload = buildExceptionTelemetryPayload(error, context)
  const telemetryError = createTelemetryError(error, payload)

  if (shouldThrottleClientException(payload)) {
    return {
      captured: false,
      deduped: true,
      payload
    }
  }

  captureSentryClientException(telemetryError, payload)

  try {
    if (typeof client.captureException === 'function') {
      client.captureException(telemetryError, payload)
      return {
        captured: true,
        deduped: false,
        payload
      }
    }
  } catch {
    // Fall back to a direct event below.
  }

  try {
    if (typeof client.capture === 'function') {
      const posthogPayload = buildPostHogFallbackPayload(
        client,
        telemetryError,
        payload
      )

      client.capture('$exception', posthogPayload)
    }
  } catch {
    // Keep the boundary stable even when analytics is unavailable.
  }

  return {
    captured: true,
    deduped: false,
    payload
  }
}

function captureActivationException(
  error: unknown,
  context: BaseActivationExceptionContext & {
    activationSurface: ActivationSurface
    activationStage: string
    source: string
  }
) {
  return captureClientException(error, context)
}

export function capturePublicChatFirstResponseException(
  error: unknown,
  context: PublicChatFirstResponseExceptionContext = {}
) {
  const {
    source = 'public-chat-first-response',
    stage = 'first_response',
    ...restContext
  } = context

  return captureActivationException(error, {
    ...restContext,
    source,
    activationSurface: 'public_chat',
    activationStage: stage
  })
}

export function captureSharedChatFollowUpException(
  error: unknown,
  context: SharedChatFollowUpExceptionContext = {}
) {
  const {
    source = 'shared-chat-follow-up',
    stage = 'follow_up_send',
    ...restContext
  } = context

  return captureActivationException(error, {
    ...restContext,
    source,
    activationSurface: 'shared_chat',
    activationStage: stage
  })
}
